<template>
  <div>
    <div
      v-if="!occurrences.length"
      class="text-center py-4"
    >
      <feather-icon
        v-if="emptyIcon"
        :icon="emptyIcon"
        size="40"
        class="text-muted mb-2"
      />
      <p class="text-muted mb-0">
        {{ emptyText }}
      </p>
    </div>

    <div
      v-else
      class="occ-table-wrapper"
    >
      <table class="table table-hover mb-0 occ-table">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              :class="[
                col.sticky ? `sticky-${col.sticky}` : null,
                col.align === 'center' ? 'text-center' : null,
                col.sortable === false ? null : 'sortable',
              ]"
              :style="col.width ? { width: col.width } : null"
              @click="col.sortable === false ? null : toggleSort(col.key)"
            >
              {{ col.label }}
              <feather-icon
                v-if="sortKey === col.key"
                :icon="sortDir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="12"
              />
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="occ in occurrences"
            :key="occ.id"
          >
            <td class="sticky-left">
              <span
                class="badge text-monospace"
                :class="{ 'protocol-link': podeAbrir }"
                :style="protocolBadgeStyle(occ)"
                :role="podeAbrir ? 'button' : null"
                :tabindex="podeAbrir ? 0 : null"
                :title="podeAbrir ? 'Abrir ocorrência' : null"
                @click="abrir(occ)"
                @keydown.enter.prevent="abrir(occ)"
                @keydown.space.prevent="abrir(occ)"
              >{{ occ.protocol }}</span>
            </td>
            <td>{{ occ.requester_name }}</td>
            <td>{{ occ.requester_phone || "-" }}</td>
            <td
              class="cell-truncate"
              :title="fullAddress(occ)"
            >
              <span v-if="occ.address">
                {{ occ.address }}{{ occ.number ? ", " + occ.number : "" }}
                <span
                  v-if="occ.neighborhood"
                  class="text-muted"
                >&mdash; {{ occ.neighborhood }}</span>
              </span>
              <span
                v-else
                class="text-muted"
              >-</span>
            </td>
            <td>{{ occ.dispatch_type ? occ.dispatch_type.name : "-" }}</td>
            <td>{{ occ.dispatch_reason ? occ.dispatch_reason.name : "-" }}</td>
            <td>{{ occ.discipline ? occ.discipline.name : "-" }}</td>
            <td>{{ responsibleName(occ) }}</td>
            <td>
              <span
                class="badge"
                :style="stepBadgeStyle(occ.current_step, occ)"
              >
                {{ stepLabel(occ.current_step, occ) }}
              </span>
            </td>
            <td class="text-nowrap">
              {{ formatDate(occ.created_at) }}
            </td>
            <td>
              <span
                :class="elapsedBadgeClass(
                  occ.current_step_since || occ.created_at,
                  occ.current_step,
                  occ.discipline_id,
                )"
              >
                {{ elapsedHHMMSS(occ.current_step_since || occ.created_at) }}
              </span>
            </td>
            <td class="text-center sticky-right">
              <button
                v-if="podeAbrir"
                class="btn btn-sm btn-primary"
                @click="abrir(occ)"
              >
                <feather-icon
                  icon="EyeIcon"
                  size="13"
                  class="mr-25"
                />
                Visualizar
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import ElapsedTimeMixin from '@/mixins/ElapsedTimeMixin'
import { stepLabel as occurrenceStepLabel } from '@/utils/occurrenceStep'

export default {
  name: 'OccurrenceTable',

  mixins: [ElapsedTimeMixin],

  props: {
    occurrences: {
      type: Array,
      required: true,
    },
    emptyText: {
      type: String,
      default: 'Nenhuma ocorrência.',
    },
    emptyIcon: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      sortKey: null,
      sortDir: 'asc',
      columns: [
        { key: 'protocol', label: 'Protocolo', sticky: 'left' },
        { key: 'requester_name', label: 'Solicitante' },
        { key: 'requester_phone', label: 'Telefone' },
        { key: 'address', label: 'Endereço' },
        { key: 'dispatch_type', label: 'Tipo' },
        { key: 'dispatch_reason', label: 'Motivo' },
        { key: 'discipline', label: 'Disciplina' },
        { key: 'responsible', label: 'Responsável' },
        { key: 'current_step', label: 'Etapa' },
        { key: 'created_at', label: 'Aberta em' },
        { key: 'elapsed', label: 'Tempo na Etapa' },
        {
          key: 'actions', label: 'Ações', sortable: false, sticky: 'right', align: 'center', width: '120px',
        },
      ],
    }
  },

  computed: {
    podeAbrir() {
      return this.$hasAbility('regulacao.visualizar')
    },

  },

  methods: {
    abrir(occ) {
      if (!this.podeAbrir) return

      this.$emit('open', occ)
    },

    // ── Ordenação ───────────────────────────────────────────────────────
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'asc'
      }

      this.emitChange()
    },

    emitChange() {
      this.$emit('change', {
        sortBy: this.sortKey,
        sortDir: this.sortDir,
      })
    },

    // ── Células ─────────────────────────────────────────────────────────
    fullAddress(occ) {
      if (!occ.address) return ''

      const numero = occ.number ? `, ${occ.number}` : ''
      const bairro = occ.neighborhood ? ` — ${occ.neighborhood}` : ''

      return `${occ.address}${numero}${bairro}`
    },

    responsibleName(occ) {
      return occ.active_responsibility && occ.active_responsibility.user
        ? occ.active_responsibility.user.name
        : '—'
    },

    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('pt-BR')
    },

    protocolBadgeStyle(occ) {
      if (occ.classification && occ.classification.hex_color) {
        return { backgroundColor: occ.classification.hex_color, color: '#fff' }
      }
      return { backgroundColor: '#7367f0', color: '#fff' }
    },

    stepLabel(step, occ) {
      return occurrenceStepLabel(step, occ)
    },

    stepBadgeStyle(step, occ) {
      if (step === 'despacha') {
        return occ && occ.dispatches_count > 0
          ? { backgroundColor: '#2563eb', color: '#fff' }
          : { backgroundColor: '#f59e0b', color: '#fff' }
      }
      const colors = {
        regulacao: { backgroundColor: '#2563eb', color: '#fff' },
      }
      return colors[step] || { backgroundColor: '#6b7280', color: '#fff' }
    },
  },
}
</script>

<style scoped>
.occ-table-wrapper {
  max-height: 60vh;
  overflow: auto;
}

.occ-table {
  font-size: 0.85rem;
}
.occ-table th,
.occ-table td {
  padding: 0.5rem 0.75rem;
  vertical-align: middle;
}

.occ-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f3f2f7;
  white-space: nowrap;
  border-top: none;
}
.dark-layout .occ-table thead th {
  background: #343d55;
}

.occ-table th.sortable {
  cursor: pointer;
  user-select: none;
}
.occ-table th.sortable:hover {
  color: #7367f0;
}

.occ-table th.sticky-left,
.occ-table td.sticky-left {
  position: sticky;
  left: 0;
  background: #fff;
}
.occ-table th.sticky-right,
.occ-table td.sticky-right {
  position: sticky;
  right: 0;
  background: #fff;
}
.occ-table td.sticky-left,
.occ-table td.sticky-right {
  z-index: 2;
}
.occ-table thead th.sticky-left,
.occ-table thead th.sticky-right {
  z-index: 4;
  background: #f3f2f7;
}
.occ-table td.sticky-left {
  box-shadow: 1px 0 0 #ebe9f1;
}
.occ-table td.sticky-right {
  box-shadow: -1px 0 0 #ebe9f1;
}

.occ-table tbody tr:hover td.sticky-left,
.occ-table tbody tr:hover td.sticky-right {
  background: #f6f6f9;
}
.dark-layout .occ-table td.sticky-left,
.dark-layout .occ-table td.sticky-right {
  background: #283046;
}
.dark-layout .occ-table thead th.sticky-left,
.dark-layout .occ-table thead th.sticky-right {
  background: #343d55;
}
.dark-layout .occ-table tbody tr:hover td.sticky-left,
.dark-layout .occ-table tbody tr:hover td.sticky-right {
  background: #242b3d;
}
.dark-layout .occ-table td.sticky-left {
  box-shadow: 1px 0 0 #3b4253;
}
.dark-layout .occ-table td.sticky-right {
  box-shadow: -1px 0 0 #3b4253;
}

.protocol-link {
  cursor: pointer;
}
.protocol-link:hover {
  filter: brightness(1.12);
  text-decoration: underline;
}
.protocol-link:focus-visible {
  outline: 2px solid #7367f0;
  outline-offset: 1px;
}

.cell-truncate {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
